import { AxiosResponse } from "axios";
import _ from "lodash";
import queryString from "query-string";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";
import { Modules } from "src/types/osbuilder";

import { unpackError } from "@dashboard/devices/api/helpers";
import apiClient from "@dashboard/files/api/apiClient";
import { BuildArtifact } from "@dashboard/files/types";
import { AndroidModule, Build, Product } from "@dashboard/products/types";
import AuthService from "@services/authService";

function serializeModule(module: Modules[number]): AndroidModule {
    const tags = ["Added"];

    if ("tags" in module && Array.isArray(module.tags)) {
        tags.push(...(module.tags as string[]));
    }
    return { ...module, name: module.name as string, tags: _.uniq(tags) };
}

export const serializeProduct = (products: API.Product[]): Product[] => {
    return products.map(({ created: _created, numberOfBuilds: _numberOfBuilds, ...rest }) => {
        const response = { ...rest };

        if (response.customBuildConfig && !response.customBuildConfig.manifest) {
            response.customBuildConfig.manifest = {};
        }
        if (response.customBuildConfig && response.customBuildConfig.manifest && !response.customBuildConfig.manifest.projects) {
            response.customBuildConfig.manifest.projects = [];
        }
        if (response.customBuildConfig && !response.customBuildConfig.sources) {
            response.customBuildConfig.sources = {};
        }
        if (response.customBuildConfig && !response.customBuildConfig.modules) {
            response.customBuildConfig.modules = [];
        }
        if (response.customBuildConfig && !response.customBuildConfig.uiSettings) {
            response.customBuildConfig.uiSettings = {
                headlessMode: "no",
                kioskMode: "no",
            };
        }
        if (response.customBuildConfig && !response.customBuildConfig.buildVariant) {
            response.customBuildConfig.buildVariant = "user";
        }
        if (response.customBuildConfig && response.customBuildConfig.modules) {
            response.customBuildConfig.modules = response.customBuildConfig.modules.map(serializeModule);
        }

        return response as Product;
    });
};

const addDownloadLink = async (item: API.BuildArtifact): Promise<BuildArtifact> => {
    try {
        const link = await apiClient.getFileLink(item.id);
        return { ...item, link };
    } catch (_error) {
        return { ...item, link: null };
    }
};

export const fetchFileDownloadLinks = async (build: API.Build): Promise<Build> => {
    if (build.artifacts?.length) {
        const artifacts = await Promise.all(build.artifacts.map(addDownloadLink));
        return { ...build, artifacts };
    }
    return { ...build, artifacts: [] };
};

const serializeBuild = (build: API.Build): Build => ({
    ...build,
    artifacts: (build.artifacts ?? []).map((item) => ({
        ...item,
        link: "",
    })),
});

export const fetchProductBuilds = async (productId: number): Promise<Build[]> => {
    const endpoint = await RouteService.getProductRoute();
    try {
        const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<API.Build[]>>>(
            `${endpoint}products/${productId}/builds`,
        );
        return data.model.map(serializeBuild);
    } catch (error) {
        throw unpackError(error);
    }
};

const requestFetchBuilds = async (payload: Product[]): Promise<Build[]> => {
    const buildPromises = payload.map((product) => fetchProductBuilds(product.id));
    const buildArrays = await Promise.all(buildPromises);
    return buildArrays.flat();
};

export type FetchProductsPayload = {
    workspaceId: number;
    isTerminated?: boolean;
};
export const fetchProducts = async (payload: FetchProductsPayload): Promise<Product[]> => {
    if (payload.isTerminated) {
        return [];
    }
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<API.Product[]>>>(endpoint + "products", {
        params: { workspaceId: payload.workspaceId },
    });
    return serializeProduct(data.model);
};
export const fetchProductsWithBuilds = async (payload: FetchProductsPayload) => {
    const products = await fetchProducts(payload);
    const builds = await requestFetchBuilds(products);
    return { products, builds };
};

interface FetchProductChangeLogsPayload {
    id: number;
}

export const fetchProductChangeLogs = async (payload: FetchProductChangeLogsPayload) => {
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<API.Product["changes"]>>>(
        `${endpoint}products/${payload.id}/changes`,
    );

    return data.model;
};

interface BuildChangesPayload {
    productId: number;
    version: string;
    changes: string[];
}
export const setBuildChanges = async (payload: BuildChangesPayload): Promise<API.Product["changes"]> => {
    try {
        const formData = new FormData();
        formData.append("version", payload.version);
        payload.changes.forEach((change) => formData.append("changes", change));

        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.patch<FormData, AxiosResponse<ApiResponse<API.Product["changes"]>>>(
            `${endpoint}products/${payload.productId}/changes`,
            formData,
            { headers: { "content-type": "multipart/form-data" } },
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface DeleteProductChangesPayload {
    productId: number;
    version: string;
}

export const deleteProductChangelog = async (payload: DeleteProductChangesPayload): Promise<API.Product["changes"]> => {
    try {
        const formData = new FormData();
        formData.append("version", payload.version);

        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.delete<FormData, AxiosResponse<ApiResponse<API.Product["changes"]>>>(
            `${endpoint}products/${payload.productId}/changes`,
            {
                headers: { "content-type": "multipart/form-data" },
                data: formData,
            },
        );

        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

export const fetchAllProducts = async () => {
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<API.Product[]>>>(endpoint + "products/all");

    return data.model;
};

export const createProduct = async (payload: Product) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.post<string, AxiosResponse<ApiResponse<API.Product>>>(
            endpoint + "products",
            JSON.stringify(payload),
            { headers: { "content-type": "application/json" } },
        );
        return serializeProduct([data.model])[0];
    } catch (error) {
        throw unpackError(error);
    }
};
export const updateProduct = async (payload: Partial<Product>) => {
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.patch<string, AxiosResponse<ApiResponse<API.Product>>>(
        endpoint + "products",
        JSON.stringify(payload),
        { headers: { "content-type": "application/json" } },
    );
    return serializeProduct([data.model])[0];
};
export const updateProductCodename = async (productId: number, newCodename: string) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.patch<string, AxiosResponse<ApiResponse<API.Product>>>(
            `${endpoint}products/${productId}/codename`,
            queryString.stringify({ newCodename }),
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};
export const updateProductAndroidVersion = async (productId: number, newVersion: string) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.patch<string, AxiosResponse<ApiResponse<API.Product>>>(
            `${endpoint}products/${productId}/version`,
            queryString.stringify({ newVersion }),
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

export const deleteProduct = async (payload: number) => {
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.delete<string, AxiosResponse<BasicApiResponse>>(`${endpoint}products/${payload}`);
    return data.message;
};

export type BuildConfig = {
    version: string;
    changes: string[];
    configSnapshot: {
        [key: string]: unknown;
    };
    rolloutPercentage: number;
    configOverride: {
        manifest?: {
            fromVersion?: string;
        };
        ota?: {
            incrementalFromVersions?: string[];
        };
        buildVariant?: "user" | "userdebug" | "eng";
        compatibility?: string;
    };
};

type CreateBuildPayload = {
    productId: number;
    config?: Partial<BuildConfig>;
};

export const createBuild = async ({ productId, config = {} }: CreateBuildPayload) => {
    const endpoint = await RouteService.getProductRoute();
    try {
        const { data } = await HttpService.post<string, AxiosResponse<ApiResponse<API.Build>>>(
            `${endpoint}products/${productId}/build`,
            JSON.stringify(config),
            { headers: { "content-type": "application/json" } },
        );
        return serializeBuild(data.model);
    } catch (error) {
        throw unpackError(error);
    }
};
export const cancelBuild = async (payload: Build["buildId"]) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.delete<string, AxiosResponse<BasicApiResponse>>(`${endpoint}products/build`, {
            headers: { "content-type": "application/x-www-form-urlencoded" },
            data: queryString.stringify({ buildId: payload }),
        });
        return payload;
    } catch (error) {
        throw unpackError(error);
    }
};
export const deleteBuild = async (payload: { productId: Build["productId"]; buildId: Build["buildId"] }) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.delete<string, AxiosResponse<BasicApiResponse>>(
            `${endpoint}products/${payload.productId}/builds/${payload.buildId}`,
        );
        return payload.buildId;
    } catch (error) {
        throw unpackError(error);
    }
};

export const requestDownloadBuildLog = async (payload: Build["buildId"]) => {
    const endpoint = await RouteService.getProductRoute();
    return HttpService.get<string, AxiosResponse<Blob>>(endpoint + "logging/full", {
        responseType: "blob",
        params: { buildId: payload },
    });
};

export const requestStreamBuildLog = async (buildId: Build["buildId"], token: string) => {
    const endpoint = await RouteService.getProductRoute();
    return fetch(`${endpoint}logging/stream?buildId=${buildId}`, {
        headers: {
            "Authorization": `Bearer ${token}`,
            "Accept": "text/plain",
            "Connection": "keep-alive",
            "Keep-Alive": "timeout=5, max=1000",
        },
    });
};
interface UpdateBuildMetadataPayload {
    productId: number;
    buildId: number;
    metadata: { tags: API.Tag[] };
}
export const updateBuildMetadata = async (payload: UpdateBuildMetadataPayload) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.patch<UpdateBuildMetadataPayload["metadata"], AxiosResponse<ApiResponse<API.Build>>>(
            `${endpoint}products/${payload.productId}/builds/${payload.buildId}/metadata`,
            payload.metadata,
        );
        return payload;
    } catch (error) {
        throw unpackError(error);
    }
};
interface UpdateBuildChannelPayload {
    productId: number;
    buildId: number;
    channel: Build["channel"];
}
export const updateBuildChannel = async (payload: UpdateBuildChannelPayload) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.patch<UpdateBuildChannelPayload["channel"], AxiosResponse<ApiResponse<API.Build>>>(
            `${endpoint}products/${payload.productId}/builds/${payload.buildId}/channel`,
            payload.channel,
            { headers: { "content-type": "application/json" } },
        );
        return payload;
    } catch (error) {
        throw unpackError(error);
    }
};
interface UpdateBuildRolloutPayload {
    productId: number;
    buildId: number;
    rolloutPercentage: Build["rolloutPercentage"];
}
export const updateBuildRollout = async (payload: UpdateBuildRolloutPayload) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.patch<UpdateBuildRolloutPayload["rolloutPercentage"], AxiosResponse<ApiResponse<API.Build>>>(
            `${endpoint}products/${payload.productId}/builds/${payload.buildId}/rollout`,
            payload.rolloutPercentage,
            { headers: { "content-type": "application/json" } },
        );
        return payload;
    } catch (error) {
        throw unpackError(error);
    }
};

export const fetchProductVariables = async (productId: number) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.get<PaginatedResponse<API.ProductVariable>>(
            `${endpoint}products/${productId}/variables`,
        );
        return data.list;
    } catch (error) {
        throw unpackError(error);
    }
};

interface AddVariablePayload {
    productId: number;
    variableData: {
        key: string;
        value: string;
        description?: string;
    };
}

export const addProductVariable = async (payload: AddVariablePayload) => {
    try {
        const formData = new FormData();
        formData.append("key", payload.variableData.key);
        formData.append("value", payload.variableData.value);
        if (payload.variableData.description) {
            formData.append("description", payload.variableData.description);
        }
        const endpoint = await RouteService.getProductRoute();
        const response = await HttpService.post<FormData, AxiosResponse<ApiResponse<API.ProductVariable>>>(
            `${endpoint}products/${payload.productId}/variables`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

export const deleteProductVariable = async (productId: number, variableKey: string) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const response = await HttpService.delete<BasicApiResponse>(`${endpoint}products/${productId}/variables/${variableKey}`);
        return response.data.message;
    } catch (error) {
        throw unpackError(error);
    }
};

export const updateProductVisibility = async (productId: number, visibility: string[]) => {
    try {
        const endpoint = await RouteService.getProductRoute();
        const { data } = await HttpService.patch<string, AxiosResponse<ApiResponse<API.Product>>>(
            `${endpoint}products/${productId}/visibility`,
            JSON.stringify({ visibility }),
            { headers: { "content-type": "application/json" } },
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

type ImportProductBuildPayload = {
    productId: number;
    product: Product;
    build: Build;
};
type ImportProductBuildResponse = {
    product: Product;
    build: Build;
    uploadURLs: {
        id: number;
        uploadURL: string;
    }[];
};
export const importProductBuild = async ({ productId, ...payload }: ImportProductBuildPayload) => {
    const endpoint = await RouteService.getProductRoute();
    const { data } = await HttpService.put<string, AxiosResponse<ApiResponse<ImportProductBuildResponse>>>(
        `${endpoint}products/${productId}/builds`,
        payload,
    );
    return data.model;
};

export const downloadBuild = async (productId: number, buildId: number, signal: AbortSignal) => {
    const endpoint = await RouteService.getProductRoute();

    // Must use fetch instead of axios because axios doesn't have access to headers when downloading files
    return fetch(`${endpoint}products/${productId}/builds/${buildId}/export`, {
        headers: { "Authorization": `Bearer ${AuthService.getSession()?.access_token}` },
        signal,
    });
};

interface UpdateFileCompatibilityPayload {
    fileId: number;
    compatibility: string;
}

export const updateFullOtaCompatibility = async (
    payload: UpdateFileCompatibilityPayload,
): Promise<UpdateFileCompatibilityPayload> => {
    try {
        const endpoint = await RouteService.getProductRoute();
        await HttpService.patch<{ compatibility: string }, AxiosResponse<BasicApiResponse>>(
            `${endpoint}files/${payload.fileId}/compatibility`,
            { compatibility: payload.compatibility },
            { headers: { "content-type": "application/json" } },
        );
        return payload;
    } catch (error) {
        throw unpackError(error);
    }
};

export const fetchVisibleProducts = async () => {
    try {
        const endpoint = await RouteService.getSettingsRoute();
        const { data } = await HttpService.get<string, AxiosResponse<PaginatedResponse<API.VisibleProduct>>>(
            endpoint + "products?visibility=PublicDeviceRegistration",
        );
        return data.list;
    } catch (error) {
        throw unpackError(error);
    }
};
